import { useState } from "react";
import { Alert, Button, Card, Group, Stack, Tabs, Text, TextInput, Textarea } from "@mantine/core";

import { t, type MessageKey } from "@/i18n";
import {
  WORDING_MESSAGES,
  emptyWording,
  type WordingDraft,
  type WordingMessageKey,
} from "@/pages/branding/draft";

/**
 * The lab's own patient email wording, per language (B1, D8, D17).
 *
 * A language is used only when all three emails are written, which the server checks on
 * save (`wording_incomplete`). `{link}` and `{pin}` are required in every body and refused
 * in every subject, where they would show on a lock screen. Live on save, audited (D12).
 */

const MESSAGE_LABEL: Record<WordingMessageKey, MessageKey> = {
  report_ready: "branding.wording_report_ready",
  report_updated: "branding.wording_report_updated",
  pin_reissued: "branding.wording_pin_reissued",
};

const LANGUAGE = /^[a-z]{2,3}$/;

interface WordingEditorProps {
  wording: Record<string, WordingDraft>;
  onChange: (wording: Record<string, WordingDraft>) => void;
  /** Server field codes, keyed by path (`email_wording.de.report_ready.body`). */
  errorFor: (path: string) => string | null;
  /** Languages the organisation inherits from a parent and does not set itself. */
  inheritedLanguages: { language: string; source: string | null }[];
}

export function WordingEditor({
  wording,
  onChange,
  errorFor,
  inheritedLanguages,
}: WordingEditorProps): JSX.Element {
  const languages = Object.keys(wording);
  const [active, setActive] = useState<string | null>(languages[0] ?? null);
  const [newLanguage, setNewLanguage] = useState("");
  const current = active !== null && active in wording ? active : (languages[0] ?? null);
  const newValid = LANGUAGE.test(newLanguage) && newLanguage !== "und" && !(newLanguage in wording);

  function add(): void {
    if (!newValid) return;
    onChange({ ...wording, [newLanguage]: emptyWording() });
    setActive(newLanguage);
    setNewLanguage("");
  }

  function remove(language: string): void {
    const next = { ...wording };
    delete next[language];
    onChange(next);
    setActive(Object.keys(next)[0] ?? null);
  }

  function update(
    language: string,
    key: WordingMessageKey,
    part: "subject" | "body" | "body_one_day",
    value: string,
  ): void {
    const set = wording[language] ?? emptyWording();
    onChange({ ...wording, [language]: { ...set, [key]: { ...set[key], [part]: value } } });
  }

  return (
    <Stack gap="sm" data-testid="branding-wording">
      <Text size="sm" c="dimmed">
        {t("branding.wording_hint")}
      </Text>
      {inheritedLanguages.length > 0 && (
        <Text size="sm" c="dimmed" data-testid="branding-wording-inherited">
          {t("branding.wording_inherited")}{" "}
          {inheritedLanguages.map((l) => `${l.language} (${l.source ?? "?"})`).join(", ")}
        </Text>
      )}
      <Group align="flex-end">
        <TextInput
          label={t("branding.wording_add_language")}
          description={t("branding.wording_language_hint")}
          value={newLanguage}
          onChange={(e) => setNewLanguage(e.currentTarget.value.trim().toLowerCase())}
          w={220}
          data-testid="branding-wording-language"
        />
        <Button
          variant="light"
          onClick={add}
          disabled={!newValid}
          data-testid="branding-wording-add"
        >
          {t("branding.wording_add")}
        </Button>
      </Group>
      {current === null ? (
        <Text size="sm" c="dimmed">
          {t("branding.wording_none")}
        </Text>
      ) : (
        <Tabs value={current} onChange={setActive}>
          <Tabs.List>
            {languages.map((language) => (
              <Tabs.Tab key={language} value={language}>
                {language}
              </Tabs.Tab>
            ))}
          </Tabs.List>
          {languages.map((language) => (
            <Tabs.Panel key={language} value={language} pt="sm">
              <Stack gap="sm" dir={language === "ar" ? "rtl" : "ltr"}>
                {errorFor(`email_wording.${language}`) && (
                  <Alert color="red" p="xs">
                    {errorFor(`email_wording.${language}`)}
                  </Alert>
                )}
                {WORDING_MESSAGES.map((key) => {
                  const path = `email_wording.${language}.${key}`;
                  const message = (wording[language] ?? emptyWording())[key];
                  return (
                    <Card key={key} withBorder padding="sm">
                      <Text fw={600} size="sm" mb={4}>
                        {t(MESSAGE_LABEL[key])}
                      </Text>
                      {errorFor(path) && (
                        <Alert color="red" p="xs" mb="xs">
                          {errorFor(path)}
                        </Alert>
                      )}
                      <TextInput
                        label={t("branding.wording_subject")}
                        value={message.subject}
                        onChange={(e) => update(language, key, "subject", e.currentTarget.value)}
                        error={errorFor(`${path}.subject`)}
                        data-testid={`wording-${language}-${key}-subject`}
                      />
                      <Textarea
                        label={t("branding.wording_body")}
                        description={t("branding.wording_body_hint")}
                        autosize
                        minRows={3}
                        value={message.body}
                        onChange={(e) => update(language, key, "body", e.currentTarget.value)}
                        error={errorFor(`${path}.body`)}
                        data-testid={`wording-${language}-${key}-body`}
                      />
                      {key === "pin_reissued" && (
                        <Textarea
                          label={t("branding.wording_body_one_day")}
                          autosize
                          minRows={2}
                          value={message.body_one_day}
                          onChange={(e) =>
                            update(language, key, "body_one_day", e.currentTarget.value)
                          }
                          error={errorFor(`${path}.body_one_day`)}
                          data-testid={`wording-${language}-${key}-body_one_day`}
                        />
                      )}
                    </Card>
                  );
                })}
                <Group>
                  <Button variant="subtle" color="gray" onClick={() => remove(language)}>
                    {t("branding.wording_remove_language")} {language}
                  </Button>
                </Group>
              </Stack>
            </Tabs.Panel>
          ))}
        </Tabs>
      )}
    </Stack>
  );
}
